import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { api } from "./api";

const AuthCtx = createContext(null);

// Role-based redirect destinations
const ROLE_HOME = {
    admin: "/admin",
    customer: "/profile",
};

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);

        // Role অনুযায়ী সঠিক জায়গায় পাঠাও
        window.location.href = ROLE_HOME[data.role] ?? "/";

        return data;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}

        setUser(null);
        window.location.href = "/";
    };

    return (
        <AuthCtx.Provider value={{ user, loading, login, logout, refresh }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    return useContext(AuthCtx);
}

/**
 * ProtectedRoute — শুধুমাত্র নির্দিষ্ট role এর user ঢুকতে পারবে।
 *
 * ব্যবহার:
 *   <ProtectedRoute role="admin">   → শুধু admin দেখতে পাবে
 *   <ProtectedRoute role="customer"> → শুধু customer দেখতে পাবে
 *   <ProtectedRoute>                → যেকোনো logged-in user দেখতে পাবে
 */
export function ProtectedRoute({ children, role }) {
    const { user, loading } = useAuth();
    const location = useLocation();

    // Auth check চলাকালীন কিছু দেখাবো না
    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <span>Loading...</span>
            </div>
        );
    }

    // Login করা নেই → Login page এ পাঠাও, পরে ফিরে আসার জন্য current path মনে রাখো
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // নির্দিষ্ট role লাগবে কিন্তু user এর role মিলছে না
    if (role && user.role !== role) {
        // তাকে তার নিজের home এ পাঠিয়ে দাও
        return <Navigate to={ROLE_HOME[user.role] ?? "/"} replace />;
    }

    return children;
}